import {
  IDeviceAssessmentResultsResponseDataRaw,
  IDeviceImageListGradeProResponseDataRaw,
  IDeviceImageListResponseDataRaw,
  IDeviceImagesListData,
  IImageListItem,
  IInvalidDeviceAssessmentResponseData,
  IRetakeReason,
  IValidateDeviceAssessmentData,
  IValidateDeviceAssessmentResponseDataRaw,
  TImageUploadStatus,
} from './types';

/**
 * Transforms the images list to the format expected by the UI.
 */
export function formatDeviceImageUploadsListApple(
  responseData: IDeviceImageListResponseDataRaw['data']
): IDeviceImagesListData {
  const { tokens: imagesList, nlAssetName: productName } = responseData || {};

  const updatedImagesList: IImageListItem[] = imagesList.map((image) => {
    const status = image?.status?.toLowerCase() as TImageUploadStatus;
    const { retakeReasons } = image;
    const imageRetakeReasons: IRetakeReason[] = [];

    /**
     * Checks the status of an image upload and processes retake reasons if
     * the status is 're_upload'.
     * If the status is 're_upload', each retake reason object is extracted and formatted
     * for a specific position in different languages.
     */
    if (status === 're_upload') {
      retakeReasons.forEach((reasons) => {
        if (reasons) {
          const { reason, description } = reasons;
          const reasonEn = reason.en;
          const descriptionEn = description.en;

          /**
           * Format the retake reason object for a specific position in different languages.
           */
          const retakeReason = {
            _id: reasons._id,
            reason: {
              en: reasonEn,
              ar: reason.ar,
              he: reason.he || reasonEn,
              fr: reason.fr || reasonEn,
              ro: reason.ro || reasonEn,
              nl: reason.nl || reasonEn,
              pl: reason.pl || reasonEn,
            },
            description: {
              en: descriptionEn,
              ar: description.ar,
              he: description.he || descriptionEn,
              fr: description.fr || descriptionEn,
              ro: description.ro || descriptionEn,
              nl: description.nl || descriptionEn,
              pl: description.pl || descriptionEn,
            },
          };
          imageRetakeReasons.push(retakeReason);
        }
      });
    }

    return {
      ...image,
      status,
      imageRetakeReasons,
    };
  });

  return { images: updatedImagesList, productName };
}

/**
 * Transforms the images list to the format expected by the UI.
 */
export function formatDeviceImageUploadsListGradePro(
  responseData: IDeviceImageListGradeProResponseDataRaw['data']
) {
  // TODO: Pass down the product name to the screens later.
  const {
    images: imagesList,
    productName: _,
    nlAssetName: productName,
  } = responseData || {};

  const updatedImagesList: IImageListItem[] = imagesList.map((image) => {
    const {
      imageId: id,
      fileUploadedPath: url = '',
      uploadedOn,
      retakeReasons,
      position,
      comment,
    } = image;
    const status = image?.status?.toLowerCase() as TImageUploadStatus;

    const imageRetakeReasons: IRetakeReason[] = [];

    /**
     * Checks the status of an image upload and processes retake reasons if
     * the status is 're_upload'.
     * If the status is 're_upload', each retake reason object is extracted and formatted
     * for a specific position in different languages.
     */
    if (status === 're_upload') {
      retakeReasons.forEach((reasons) => {
        if (reasons) {
          const { reason, description } = reasons;
          const reasonEn = reason.en;
          const descriptionEn = description.en;

          /**
           * Format the retake reason object for a specific position in different languages.
           */
          const retakeReason = {
            _id: reasons._id,
            reason: {
              en: reasonEn,
              ar: reason.ar,
              he: reason.he || reasonEn,
              fr: reason.fr || reasonEn,
              ro: reason.ro || reasonEn,
              nl: reason.nl || reasonEn,
              pl: reason.pl || reasonEn,
            },
            description: {
              en: descriptionEn,
              ar: description.ar,
              he: description.he || descriptionEn,
              fr: description.fr || descriptionEn,
              ro: description.ro || descriptionEn,
              nl: description.nl || descriptionEn,
              pl: description.pl || descriptionEn,
            },
          };
          imageRetakeReasons.push(retakeReason);
        }
      });
    }

    return {
      comment,
      id,
      imageRetakeReasons,
      status,
      url,
      uploadedOn,
      metadata: {
        color: 'ANY',
        position,
        size: 'Cropped',
      },
    };
  });

  return { images: updatedImagesList, productName };
}

/**
 * Transforms the validation response to the format expected by the UI.
 */
export function formatValidateDeviceAssessmentResponse(
  responseData: IValidateDeviceAssessmentResponseDataRaw['data']
): IValidateDeviceAssessmentData {
  const { valid: isValid, asset } = responseData || {};
  const assetLabel = asset?.label;
  const assetBrandName = asset?.brandName;
  const brandName = {
    en: assetBrandName,
    ar: asset?.brandName_ar || assetBrandName,
    he: asset?.brandName_he || assetBrandName,
    fr: asset?.brandName_fr || assetBrandName,
    ro: asset?.brandName_ro || assetBrandName,
    nl: asset?.brandName_nl || assetBrandName,
    pl: asset?.brandName_pl || assetBrandName,
  };
  const productName = {
    en: assetLabel,
    ar: asset?.label_ar || assetLabel,
    he: asset?.label_he || assetLabel,
    fr: asset?.label_fr || assetLabel,
    ro: asset?.label_ro || assetLabel,
    nl: asset?.label_nl || assetLabel,
    pl: asset?.label_pl || assetLabel,
  };

  return { brandName, isValid, productName };
}

/**
 * Transforms the validation response to the format expected by the UI.
 */
export function formatInvalidDeviceAssessmentResponse(
  data: IInvalidDeviceAssessmentResponseData
) {
  const { sessionStatus } = data || {};

  return {
    brandName: undefined,
    isAssessmentDone: sessionStatus && sessionStatus === 'ASSESSMENT_DONE',
    isValid:
      sessionStatus &&
      sessionStatus !== 'INVALID_SESSION' &&
      sessionStatus !== 'ASSESSMENT_DONE',
    productName: null,
  };
}

/**
 * Transforms the validation response to the format expected by the UI.
 */
export function formatSubmitDeviceAssessmentResultsResponse(
  responseData: IDeviceAssessmentResultsResponseDataRaw['data']
) {
  return { redirectUrl: responseData?.uri };
}
